import { Fragment, type ReactNode } from 'react';

const SAFE_HREF = /^(?:https?:\/\/|mailto:)[^\s"'<>]+$/i;

function safeHref(raw: string | null): string | null {
  if (raw === null) return null;
  const trimmed = raw.trim();
  return SAFE_HREF.test(trimmed) ? trimmed : null;
}

function renderChildren(nodes: NodeListOf<ChildNode>, keyPrefix: string): ReactNode[] {
  return [...nodes].flatMap((node, index) => {
    const rendered = renderNode(node, `${keyPrefix}-${String(index)}`);
    return Array.isArray(rendered) ? rendered : [rendered];
  }).filter((node): node is Exclude<ReactNode, null | undefined | false> => node !== null && node !== undefined && node !== false);
}

function renderNode(node: ChildNode, key: string): ReactNode | ReactNode[] {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  const children = renderChildren(element.childNodes, key);

  switch (element.tagName.toLowerCase()) {
    case 'b': return <b key={key}>{children}</b>;
    case 'strong': return <strong key={key}>{children}</strong>;
    case 'i': return <i key={key}>{children}</i>;
    case 'em': return <em key={key}>{children}</em>;
    case 'u': return <u key={key}>{children}</u>;
    case 'p': return <p key={key}>{children}</p>;
    case 'div': return <div key={key}>{children}</div>;
    case 'br': return <br key={key} />;
    case 'ul': return <ul key={key}>{children}</ul>;
    case 'ol': return <ol key={key}>{children}</ol>;
    case 'li': return <li key={key}>{children}</li>;
    case 'a': {
      const href = safeHref(element.getAttribute('href'));
      return href === null ? <Fragment key={key}>{children}</Fragment> : <a key={key} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
    default:
      return children;
  }
}

export function FormattedRequest({ html }: { readonly html: string }): React.JSX.Element {
  const template = document.createElement('template');
  template.innerHTML = html;
  return <div className="ddds-formatted-request">{renderChildren(template.content.childNodes, 'request')}</div>;
}